import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { List } from 'react-window';
import { ArrowDown, ArrowUp } from 'lucide-react';

import { selectUserToken } from '../../../../redux/features/user/userSelectors';
import {
  useGetEventLogsBySubjectQuery,
  EVENT_LOG_SUBJECTS,
} from '../../../../redux/services/eventLog/eventLogApi';

import { HistoryRow } from './HistoryRow';
import { Loader } from '../../Loader';
import { ButtonTemplate } from '../../buttons/ButtonTemplate';

const ROW_HEIGHT = 56;
const LIST_HEIGHT = 420;

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'user.whatsapp_sent', label: 'WhatsApp' },
  { value: 'user.message_sent', label: 'Messages' },
  { value: 'communication.email_sent', label: 'Emails' },
  { value: 'client.document_downloaded', label: 'Downloads' },
  { value: 'property.viewed', label: 'Views' },
  { value: 'property.shared', label: 'Shares' },
  { value: 'property.claimed', label: 'Claims' },
];

export const HistoryDialog = ({ realtyId }) => {
  const token = useSelector(selectUserToken);
  const [filter, setFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');

  const {
    data: logs = [],
    isLoading,
    error,
  } = useGetEventLogsBySubjectQuery(
    {
      token,
      subjectType: EVENT_LOG_SUBJECTS.REALTY,
      subjectId: realtyId,
    },
    { skip: !token || !realtyId }
  );

  const filteredLogs = useMemo(() => {
    const filtered =
      filter === 'all' ? logs : logs.filter(l => l.event_type === filter);

    const sorted = [...filtered].sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
    });

    return sorted;
  }, [logs, filter, sortOrder]);

  return (
    <div className="flex flex-col w-[320px] sm:w-[420px]">
      <div className="flex items-center justify-between mb-3">
        <span className="text-base font-semibold text-blackColor">
          Activity
        </span>

        <ButtonTemplate
          icon={sortOrder === 'desc' ? ArrowDown : ArrowUp}
          isIconText={true}
          text={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
          color={'#100C09'}
          isSmall
          classes={
            'px-1.5 py-0.5 border-[1px] border-gray-200 bg-gray-50 hover:bg-gray-100'
          }
          handleClick={() =>
            setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))
          }
        />
      </div>

      <select
        value={filter}
        onChange={e => setFilter(e.target.value)}
        className="mb-3 px-2 py-1.5 text-sm border border-gray-200 rounded bg-white text-blackColor focus:outline-none focus:border-darkBlueColor"
      >
        {FILTER_OPTIONS.map(opt => (
          <option
            key={opt.value}
            value={opt.value}
          >
            {opt.label}
          </option>
        ))}
      </select>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Loader
            width={24}
            height={24}
          />
        </div>
      )}

      {error && !isLoading && (
        <div className="py-8 text-center text-sm text-gray-500">
          Failed to load activity.
        </div>
      )}

      {!isLoading && !error && filteredLogs.length === 0 && (
        <div className="py-8 text-center text-sm text-gray-500">
          No activity yet.
        </div>
      )}

      {!isLoading && !error && filteredLogs.length > 0 && (
        <List
          rowComponent={HistoryRow}
          rowCount={filteredLogs.length}
          rowHeight={ROW_HEIGHT}
          rowProps={{ logs: filteredLogs }}
          style={{
            height: Math.min(LIST_HEIGHT, filteredLogs.length * ROW_HEIGHT),
            width: '100%',
          }}
        />
      )}

      <div className="mt-2 text-xs text-gray-500 text-right">
        {filteredLogs.length} {filteredLogs.length === 1 ? 'event' : 'events'}
      </div>
    </div>
  );
};
